import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, money, useQuery } from '../api.js';
import { Drawer, ErrorNote, Pill, Spinner, useToast } from '../ui.js';
import { Segmented } from '../controls.js';
import { PortalDocuments } from './PortalDocuments.js';
import { PortalSyllabus } from './PortalSyllabus.js';
import { PortalRequest } from './PortalRequest.js';
import { Logbook } from './Logbook.js';
import { PortalBoard } from './PortalBoard.js';
import { useLive } from '../live.js';

type PortalTab = 'schedule' | 'syllabus' | 'logbook' | 'documents';

interface PortalData {
  student: {
    id: number; name: string; firstName: string; courseName: string; regulation: string;
    instructorName: string; totalHours: number; balanceCents: number; creditCents: number;
  };
  nextLesson: { id: number; code: string; name: string; flightHours: number; groundHours: number; isSolo: boolean } | null;
  gate: { title: string; body: string } | null;
  nextReservation: {
    id: number; whenLabel: string; timeLabel: string; lessonLabel: string;
    instructorName: string; aircraftTail: string; aircraftModel: string; tiedown: string; notes: string;
  } | null;
  beforeYouFly: { label: string; note: string; urgent: boolean }[];
  requests: {
    id: number; startsAt: number; when: string; lesson: string; instructor: string; status: string;
    withinNotice: boolean; cancelRequestedAt: number | null; cancelReason: string | null;
  }[];
  cancelNoticeHours: number;
  instructors: { id: number; name: string }[];
  aircraft: { id: number; tail: string; model: string }[];
  readOnly: boolean;
}

export function Portal(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as PortalTab) || 'schedule';
  const { data, loading, error, reload } = useQuery<PortalData>('/portal');

  // A request confirmed or declined lands here without the student reloading.
  useLive('schedule', reload);
  useLive('notifications', reload);

  const setTab = (next: PortalTab) => {
    const p = new URLSearchParams(params);
    if (next === 'schedule') p.delete('tab'); else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const toast = useToast();

  const [requesting, setRequesting] = useState(false);
  // Set when a cancellation falls inside the notice window and needs a reason.
  const [cancelling, setCancelling] = useState<PortalData['requests'][number] | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const s = data.student;

  const cancel = async (id: number) => {
    try {
      const result = await api.post<{ cancelled: boolean }>(`/portal/requests/${id}/cancel`);
      toast.ok(result.cancelled ? 'Cancelled' : 'Cancellation sent to your instructor');
      reload();
    } catch (err) {
      toast.error('Could not cancel', err instanceof Error ? err.message : undefined);
    }
  };


  return (
    <div className="portal">
      <header className="portal__head">
        <div style={{ minWidth: 0 }}>
          <h1 className="portal__greeting">Hello, {s.firstName}</h1>
          <div className="muted" style={{ fontSize: 13.5 }}>
            Part {s.regulation} · {s.courseName} · {s.totalHours.toFixed(1)} hrs logged · instructor {s.instructorName}
          </div>
        </div>
        <div className="portal__balance">
          <span className="small muted" style={{ display: 'block' }}>Account balance</span>
          <strong style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>{money(s.balanceCents)}</strong>
          {s.creditCents > 0 && <span className="small muted"> · {money(s.creditCents)} credit</span>}
        </div>
      </header>

      <div className="portal__switcher">
        <Segmented
          grow
          value={tab}
          onChange={setTab}
          label="Portal sections"
          options={[
            { value: 'schedule', label: 'Schedule' },
            { value: 'syllabus', label: 'Syllabus' },
            { value: 'logbook', label: 'Logbook' },
            // Only the inbox carries a count. A badge on Documents reads as an
            // unread message when it means something else entirely; the tab's
            // own banner says how many documents are waiting for a signature.
            { value: 'documents', label: 'Documents' },
          ]}
        />
      </div>

      {tab === 'syllabus' && <PortalSyllabus studentId={s.id} />}
      {tab === 'logbook' && <Logbook studentId={s.id} embedded />}
      {tab === 'documents' && <PortalDocuments />}
      {tab === 'schedule' && (
        <>

      <div className="card" style={{ borderRadius: 12, marginTop: 14 }}>
        <div className="row" style={{
          padding: '12px 18px', borderBottom: '1px solid var(--border-3)',
          justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>My lessons and requests</span>
          <button
            className="btn btn--primary"
            disabled={data.readOnly}
            title={data.readOnly ? 'Only the student can send a request' : undefined}
            onClick={() => setRequesting(true)}
          >
            Request a lesson
          </button>
        </div>
        {data.requests.length === 0 && (
          <div className="empty">
            Nothing booked or requested. Use <strong>Request a lesson</strong> to ask for a time.
          </div>
        )}
        {data.requests.map((r) => (
          <div key={r.id} className="reqrow">
            <div className="reqrow__when">{r.when}</div>
            <div className="reqrow__what">{r.lesson}</div>
            <div className="reqrow__who muted">{r.instructor}</div>
            {/* A pending cancellation replaces the status: it is the newer fact. */}
            {!r.cancelRequestedAt && <Pill>{r.status}</Pill>}
            {r.cancelRequestedAt ? (
              <span className="pill pill--amber" style={{ flex: 'none' }}>Cancellation asked for</span>
            ) : r.status !== 'completed' ? (
              <button
                className="btn btn--link"
                onClick={() => (r.withinNotice ? setCancelling(r) : cancel(r.id))}
              >
                Cancel
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <PortalBoard studentId={s.id} />
        </>
      )}

      {cancelling && (
        <CancelRequest
          lesson={cancelling}
          noticeHours={data.cancelNoticeHours}
          onClose={() => setCancelling(null)}
          onSent={() => { setCancelling(null); reload(); }}
        />
      )}

      {requesting && (
        <PortalRequest
          data={data}
          instructorName={s.instructorName}
          onClose={() => setRequesting(false)}
          onSent={() => { setRequesting(false); reload(); }}
        />
      )}

      {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}
    </div>
  );
}

/**
 * Cancelling inside the notice window. The aircraft and the instructor's day are
 * already committed, so this asks rather than cancels, and the reason goes with it.
 */
function CancelRequest({ lesson, noticeHours, onClose, onSent }: {
  lesson: { id: number; when: string; lesson: string; instructor: string };
  noticeHours: number;
  onClose: () => void;
  onSent: () => void;
}): JSX.Element {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/requests/${lesson.id}/cancel`, { reason: reason.trim() });
      toast.ok('Sent to your instructor', 'They will confirm whether the lesson comes off the schedule.');
      onSent();
    } catch (err) {
      toast.error('Could not send that', err instanceof Error ? err.message : undefined);
      setBusy(false);
    }
  };

  return (
    <Drawer
      narrow
      title="Ask to cancel"
      subtitle={`${lesson.when} · ${lesson.lesson}`}
      onClose={onClose}
      footer={
        <>
          <span className="small muted" style={{ flex: 1, minWidth: 0 }}>
            {reason.trim().length < 10 && 'A sentence is enough.'}
          </span>
          <button className="btn btn--primary" disabled={busy || reason.trim().length < 10} onClick={send}>
            {busy ? 'Sending…' : 'Send to instructor'}
          </button>
        </>
      }
    >
      <div className="card__body stack" style={{ gap: 14 }}>
        <div style={{
          padding: '11px 13px', borderRadius: 'var(--r-md)',
          background: 'var(--tint-orange)', color: 'var(--k-pending-fg)',
          fontSize: 12.5, textWrap: 'pretty',
        }}>
          This lesson is inside {noticeHours} hours, so the aircraft and
          {lesson.instructor ? ` ${lesson.instructor}'s` : ' your instructor\'s'} day are already
          committed. They decide whether it comes off the schedule.
        </div>

        <label className="field">
          <span>Why do you need to cancel?</span>
          <textarea className="textarea" style={{ minHeight: 120 }} value={reason} autoFocus
            onChange={(e) => setReason(e.target.value)}
            placeholder="They will read this, so say what happened." />
        </label>

        <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
          The lesson stays on the schedule until your instructor answers.
        </p>
      </div>
    </Drawer>
  );
}
